"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.promoteToPlan = promoteToPlan;
const path = __importStar(require("path"));
const dist_1 = require("../../core/dist");
const aiSummarization_1 = require("./utils/aiSummarization");
const weavePlan_1 = require("./weavePlan");
const SYSTEM_PROMPT = `You are an AI assistant embedded in REslava Loom, a document-driven workflow system.
Your task: read the provided document and produce an implementation plan.
Respond with exactly this format — nothing else before or after:

TITLE: <one concise line describing the plan>

## Goal
<what this plan implements in 1-2 sentences>

## Steps
1. <first concrete implementation step>
2. <second concrete implementation step>
3. <add as many steps as needed>

## Notes
<implementation notes, gotchas, open questions — no step labels here>

CRITICAL RULES:
- The ## Steps section MUST contain a numbered list with at least one item. A plan with no steps is invalid.
- Each step must be on its own line starting with a number and period: "1. ", "2. ", etc.
- Do NOT leave ## Steps empty. If the source is vague, infer concrete steps from the goal.
- Do NOT put step descriptions in ## Notes — Notes is for gotchas and context only.`;
async function promoteToPlan(input, deps) {
    const doc = await deps.loadDoc(input.filePath);
    const { weaveId, threadId } = input.targetWeaveId
        ? { weaveId: input.targetWeaveId, threadId: input.targetThreadId }
        : deriveLocation(input.filePath, deps.loomRoot);
    let title;
    let body;
    if (input.body !== undefined) {
        body = input.body;
        title = input.title ?? doc.title;
    }
    else {
        if (!doc.content || doc.content.trim().length === 0) {
            throw new Error(`${doc.type} document is empty.`);
        }
        const label = doc.type === 'chat'
            ? 'chat conversation'
            : `${doc.type} document titled "${doc.title}"`;
        const messages = (0, aiSummarization_1.buildSummarizationMessages)(SYSTEM_PROMPT, label, doc.content);
        const reply = await deps.aiClient.complete(messages);
        ({ title, body } = (0, aiSummarization_1.parseTitleAndBody)(reply));
    }
    // For an inline body, prefer a real steps table (how plans are normally structured),
    // falling back to a numbered list. The AI path always emits a numbered list.
    const parsedSteps = input.body !== undefined
        ? ((0, dist_1.parseStepsTable)(body).length > 0 ? (0, dist_1.parseStepsTable)(body) : parseNumberedSteps(body))
        : parseNumberedSteps(body);
    if (parsedSteps.length === 0) {
        throw new Error('promoteToPlan: no steps found. A plan must have at least one step (provide a Steps table or a numbered Steps list).');
    }
    const plansDir = threadId
        ? path.join(deps.loomRoot, 'loom', weaveId, threadId, 'plans')
        : path.join(deps.loomRoot, 'loom', weaveId, 'plans');
    await deps.fs.ensureDir(plansDir);
    const existingFiles = await deps.fs.readdir(plansDir).catch(() => []);
    const planFilename = (0, dist_1.planFileName)((0, dist_1.nextOrdinal)(existingFiles, 'plan'));
    const planId = (0, dist_1.generateDocId)('plan');
    // Stamp the parent design's LIVE version as the staleness baseline. Omitting it
    // (the prior behaviour) left promoted plans with design_version undefined, so
    // `isPlanStale` (undefined < version → false) never flagged them — the inverse of
    // the create_plan constant-1 bug. Falls back to the floor when promoting outside a
    // thread or before a design exists.
    let designVersion = 1;
    if (threadId) {
        const threadPath = path.join(deps.loomRoot, 'loom', weaveId, threadId);
        const design = await (0, weavePlan_1.parentDesignVersion)(threadPath, threadId, { loadDoc: deps.loadDoc, fs: deps.fs });
        if (design)
            designVersion = design.version;
    }
    const frontmatter = (0, dist_1.createBaseFrontmatter)('plan', planId, title, doc.id);
    const planDoc = {
        ...frontmatter,
        type: 'plan',
        status: 'active',
        design_version: designVersion,
        target_version: '0.1.0',
        steps: parsedSteps,
        content: input.body !== undefined ? body : `# ${title}\n\n${body}`,
    };
    // Promoted plans are born frontmatter-native: steps live in YAML, the body table is
    // a generated view (the saver canonicalizes whatever table/list the promote source had).
    planDoc._stepsFromFrontmatter = true;
    const filePath = path.join(plansDir, planFilename);
    await deps.saveDoc(planDoc, filePath);
    return { filePath, title };
}
function parseNumberedSteps(body) {
    const match = body.match(/#{1,2} Steps\s*\n([\s\S]*?)(?=\n#{1,2}\s|$)/i);
    if (!match)
        return [];
    const steps = [];
    const taken = new Set();
    for (const line of match[1].split('\n')) {
        const m = line.match(/^\s*\d+\.\s+(.+)/);
        if (m) {
            const description = m[1].trim();
            steps.push({ id: (0, dist_1.slugifyStepId)(description, taken), order: steps.length + 1, status: 'pending', title: description, description, files_touched: [], blockedBy: [], satisfies: [] });
        }
    }
    return steps;
}
function deriveLocation(filePath, loomRoot) {
    const rel = path.relative(path.join(loomRoot, 'loom'), filePath);
    const parts = rel.split(/[\\/]/);
    if (parts.length < 2)
        throw new Error(`Cannot derive weave from path: ${rel}`);
    const weaveId = parts[0];
    if (parts.length >= 3 && parts[1] === 'chats')
        return { weaveId };
    if (parts.length >= 3)
        return { weaveId, threadId: parts[1] };
    return { weaveId };
}
